// Staff softphone authentication (roadmap #41) + single-device enforcement.
// Ported from qr-phone-frontend Login.jsx + login.service.tsx (v3.2.0.0). The
// backend contract is unchanged: POST /users/login with request_from=web returns
// { status:'success', token, data }. Identity is kept in core/globals (read by the
// signaling core through core/auth) and mirrored to localStorage so a page reload
// (or an FCM-triggered tab) keeps the session.
//
// Single-device: the backend already pins a staff (role 3) session to the latest
// login_token (see core/http forced-logout on action:logoutPage). On top of that
// we reproduce the client-owned `loggedindevice` presence tree (see deviceSession)
// so that signing in on a new device signs the previous one out in real time.
import { v4 as uuidv4 } from 'uuid'
import { globals } from '../core/globals'
import { httpRequest, setForcedLogoutHandler, setTokenRenewalHandler } from '../core/http'
import { t } from '../i18n'
import {
  getOtherActiveDevices,
  forceLogoutOtherDevices,
  markDeviceLoggedIn,
  markDeviceLoggedOut,
} from './deviceSession'
import { establishStaffRealtimeSession } from './realtimeSession'
import {
  initSso,
  getAccessToken,
  onToken,
  ensureValidToken,
  signIn as ssoSignIn,
  signOut as ssoSignOut,
} from './sso'

// Keep the shared state's token in sync with silent token refreshes.
onToken((token) => {
  globals.authToken = token
})

const SESSION_KEY = 'qr_staff_session'
const DEVICE_KEY = 'qr_device_id'
const FCM_TOKEN_KEY = 'qr_fcm_token'

export interface StaffSession {
  token: string
  email: string
  user_id: number
  userData: any
}

// STABLE PER-BROWSER DEVICE ID (THE BACKEND USES IT TO TRACK LOGGED-IN DEVICES).
export const getDeviceId = (): string => {
  let id = localStorage.getItem(DEVICE_KEY)
  if (!id) {
    id = uuidv4()
    localStorage.setItem(DEVICE_KEY, id)
  }
  return id
}

// PUSH THE IDENTITY INTO THE SHARED STATE THE SIGNALING CORE READS.
const applySession = (session: StaffSession) => {
  globals.authToken = session.token
  globals.email = session.email
  globals.user_id = session.user_id
  globals.userData = session.userData
}

const clearLocalSession = () => {
  globals.authToken = null
  globals.email = null
  globals.user_id = null
  globals.userData = null
  localStorage.removeItem(SESSION_KEY)
}

// MANUAL SIGN-OUT MARKER. Signing out ends the session at the identity service and comes
// back to /staff, where the screen runs a silent session check and restores the session
// whenever one still exists. If ending that session did not take effect (a rejected return
// address, an identity service that is momentarily unreachable), that check signs the staff
// member straight back in and sign-out looks broken. The marker survives the redirect (it is
// scoped to this tab) and tells the login screen to stop and offer sign-in instead of
// restoring, so an intentional sign-out is never undone silently.
const SIGNED_OUT_KEY = 'qr_staff_signed_out'
const markManualSignOut = () => {
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, '1')
  } catch {
    /* private mode / storage disabled: the marker is an extra guard, not the mechanism */
  }
}
export const consumeManualSignOut = (): boolean => {
  try {
    const marked = sessionStorage.getItem(SIGNED_OUT_KEY) === '1'
    if (marked) sessionStorage.removeItem(SIGNED_OUT_KEY)
    return marked
  } catch {
    return false
  }
}

export const getStoredFcmToken = (): string | null => localStorage.getItem(FCM_TOKEN_KEY)
export const setStoredFcmToken = (token: string) => localStorage.setItem(FCM_TOKEN_KEY, token)

export const isStaffAuthenticated = (): boolean => Boolean(globals.authToken && globals.email)

export interface LoginResult {
  ok: boolean
  // TRUE WHEN THE SESSION IS VALID BUT THE ACCOUNT IS ACTIVE ON ANOTHER DEVICE:
  // THE UI MUST CONFIRM BEFORE SIGNING THE OTHER DEVICE OUT (confirmOtherDeviceLogout).
  otherDeviceActive?: boolean
  // TRUE WHEN THERE IS NO ACTIVE SIGN-ON SESSION: THE UI MUST REDIRECT TO SIGN IN.
  needsSignIn?: boolean
  message?: string
}

// A LOGIN THAT SUCCEEDED AT THE BACKEND BUT IS WAITING FOR THE "OTHER DEVICE" CONFIRMATION.
let pendingSession: StaffSession | null = null

// FINALISE A LOGIN: PUBLISH THE IDENTITY, PERSIST IT, ESTABLISH THE PER-STAFF SCOPED REALTIME
// SESSION (roadmap #88), AND CLAIM THIS DEVICE IN THE PRESENCE TREE (the FCM token is
// registered by the dashboard on mount). The scoped session is established BEFORE the presence
// tree is touched so that write lands under the identity the tightened rules (once deployed)
// will key off.
const finalizeSession = async (session: StaffSession): Promise<void> => {
  applySession(session)
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  await establishStaffRealtimeSession()
  await markDeviceLoggedIn(session.user_id, getDeviceId())
}

// ESTABLISH THE STAFF SESSION FROM CORPORATE SINGLE SIGN-ON. Runs a silent
// session check; when a session exists it hydrates the account record from the
// API and claims this device. When the account is already active elsewhere the
// result carries otherDeviceActive:true and the UI must call
// confirmOtherDeviceLogout. When there is no session the result carries
// needsSignIn:true and the UI must redirect to sign in. NO TECHNOLOGY NAMES OR
// BACKEND DETAILS ARE SURFACED.
export const bootstrapStaffSession = async (): Promise<LoginResult> => {
  try {
    const authenticated = await initSso()
    if (!authenticated) {
      return { ok: false, needsSignIn: true }
    }

    const token = getAccessToken()
    if (!token) {
      return { ok: false, needsSignIn: true }
    }

    // The token must be in place before the profile call so it is sent as bearer.
    globals.authToken = token
    const response = await httpRequest('users/me', 'GET', {})
    if (!response || response.status !== 'success' || !response.data) {
      globals.authToken = null
      return { ok: false, message: t.loginFailedService }
    }

    const session: StaffSession = {
      token,
      email: response.data.email,
      user_id: response.data.user_id,
      userData: response.data,
    }

    // IS THE ACCOUNT ALREADY LOGGED IN ON ANOTHER DEVICE? IF SO, HOLD THE SESSION
    // AND ASK THE UI TO CONFIRM BEFORE SIGNING THAT DEVICE OUT.
    const others = await getOtherActiveDevices(session.user_id, getDeviceId())
    if (others.length > 0) {
      pendingSession = session
      return { ok: false, otherDeviceActive: true }
    }

    await finalizeSession(session)
    return { ok: true }
  } catch (e) {
    return { ok: false, message: t.loginFailedService }
  }
}

// REDIRECT AN UNAUTHENTICATED STAFF MEMBER TO SIGN IN (hosted page or access portal).
export const staffSignIn = (): void => {
  ssoSignIn()
}

// CONFIRM THE PENDING LOGIN: SIGN THE OTHER DEVICE(S) OUT (BACKEND + PRESENCE TREE)
// AND FINALISE THIS SESSION.
export const confirmOtherDeviceLogout = async (): Promise<LoginResult> => {
  if (!pendingSession) return { ok: false, message: t.loginFailedGeneric }
  const session = pendingSession
  try {
    await httpRequest('users/logout/othersession', 'POST', { user_id: session.user_id })
    await forceLogoutOtherDevices(session.user_id, getDeviceId())
    await finalizeSession(session)
    pendingSession = null
    return { ok: true }
  } catch (e) {
    return { ok: false, message: t.loginFailedService }
  }
}

// ABANDON A PENDING LOGIN (THE STAFF DECLINED TO SIGN THE OTHER DEVICE OUT).
export const cancelPendingLogin = (): void => {
  pendingSession = null
}

// MANUAL SIGN-OUT: RELEASE THE BACKEND SESSION AND THIS DEVICE'S PRESENCE, THEN CLEAR
// LOCAL STATE. BEST-EFFORT — LOCAL STATE IS ALWAYS CLEARED EVEN IF THE NETWORK FAILS.
export const staffLogout = async (): Promise<void> => {
  const userId = globals.user_id
  markManualSignOut()
  try {
    if (userId) await markDeviceLoggedOut(userId, getDeviceId())
  } catch {
    /* ignore */
  }
  try {
    await httpRequest('users/logout', 'POST', {})
  } catch {
    /* ignore */
  }
  clearLocalSession()
  // End the sign-on session at the identity provider (full-page redirect).
  try {
    await ssoSignOut()
  } catch {
    /* ignore */
  }
}

// FORCED SIGN-OUT (SIGNED IN ELSEWHERE / SERVER-SIDE TOKEN INVALIDATED). CLEARS LOCAL
// STATE AND RETURNS TO THE LOGIN SCREEN. DOES NOT TOUCH THE PRESENCE TREE — THE OTHER
// DEVICE ALREADY OWNS IT. GUARDED SO OVERLAPPING 401s DO NOT LOOP.
let forcedLogoutInProgress = false
export const forceStaffLogout = (): void => {
  if (forcedLogoutInProgress) return
  forcedLogoutInProgress = true
  clearLocalSession()
  try {
    window.location.hash = '#/staff'
  } catch {
    /* ignore */
  }
  // FULL RELOAD SO EVERY SUBSCRIPTION/SOCKET IS TORN DOWN CLEANLY.
  try {
    window.location.reload()
  } catch {
    /* ignore */
  }
}

// WIRE THE HTTP LAYER'S 401 action:logoutPage/logout BACKSTOP TO THE FORCED SIGN-OUT.
// (core/http stays framework-agnostic; the staff layer supplies the behaviour.)
setForcedLogoutHandler(forceStaffLogout)

// WIRE THE HTTP LAYER'S TOKEN RENEWAL TO THE SIGN-IN SESSION, so a request is
// never sent with an about-to-expire token and a 401 is retried once with a
// fresh one. This is what lets a staff member keep this open all day.
setTokenRenewalHandler(ensureValidToken)
